import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Text, VStack, Box, HStack, IconButton } from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const quotes = [
  {
    sanskrit: "यथा कामो भवेत्तथा वशमापद्यते। यथा वशं तथा कर्म। यथा कर्म तथा गतिः।",
    text: "You are what your deep, driving desire is. As your desire is, so is your will. As your will is, so is your deed. As your deed is, so is your destiny.",
    author: "Brihadaranyaka Upanishad"
  },
  {
    sanskrit: "असतो मा सद्गमय। तमसो मा ज्योतिर्गमय। मृत्योर्मामृतं गमय।",
    text: "From ignorance, lead me to truth; From darkness, lead me to light; From death, lead me to immortality.",
    author: "Brihadaranyaka Upanishad"
  },
  {
    sanskrit: "योगे युक्तो यदा चित्तं वायौ निवातस्थिते दीप इव।",
    text: "When meditation is mastered, the mind is unwavering like the flame of a lamp in a windless place.",
    author: "Bhagavad Gita"
  },
  {
    sanskrit: "यद् दृष्टम् अविज्ञातं यद् विज्ञातं अविद्यते। तद्वा अस्य तत्वम् अस्मि नित्यमुक्तस्य निर्मलत्वात्।",
    text: "That which cannot be seen but is the witness of all visions; That which cannot be heard but is the hearer of all sounds; That which cannot be thought but is the thinker behind all thoughts. That is your true Self; free from the false identities of the material world.",
    author: "Kenopanishad"
  },
  {
    sanskrit: "वसुधैव कुटुम्बकम्।",
    text: "The whole world is one family.",
    author: "Maha Upanishad"
  },
  {
    sanskrit: "यः कर्मणि विज्ञानं च समन्वारब्धे स पश्यति।",
    text: "The wise sees knowledge and action as one; they see truly.",
    author: "Bhagavad Gita"
  },
  {
    sanskrit: "यस्मिन् सर्वाणि भूतानि आत्मैवाभूद् विजानतः। तत्र को मोहः कः शोकः एकत्वमनुपश्यतः।",
    text: "He who sees all beings in his own self, and his own self in all beings, loses all fear.",
    author: "Isha Upanishad"
  },
  {
    sanskrit: "सत्यमेव जयते नानृतम्। सत्येन पन्था विततो देवयानः। येनाक्रमन्ति ऋषयो ह्याप्तकामा। यत्र तत् सत्यस्य परमं निधानम्।",
    text: "Truth alone triumphs; not falsehood. Through truth, the divine path is spread out by which the sages whose desires have been completely fulfilled, reach where that supreme treasure of Truth resides.",
    author: "Mundaka Upanishad"
  },
  {
    sanskrit: "न कर्मणा न प्रजया धनेन त्यागेनैके अमृतत्वमानशुः।",
    text: "Not by works, not by progeny, not by wealth, but by renunciation alone is immortality achieved.",
    author: "Brihadaranyaka Upanishad"
  },
  {
    sanskrit: "नायमात्मा प्रवचनेन लभ्यो न मेधया न बहुना श्रुतेन। यमेवैष वृणुते तेन लभ्यः। तस्यैष आत्मा विवृणुते तनूं स्वाम्।",
    text: "This Self cannot be attained by study, nor by intellect, nor by much hearing. Whomsoever the Self chooses, by him alone is it attained. To him, this Self reveals its own nature.",
    author: "Katha Upanishad"
  }
];

const EXIT_MS = 350;
const INTERVAL_MS = 9000;

const QuoteCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState('in'); // 'in' | 'out'
  const [isHovered, setIsHovered] = useState(false);
  const timerRef = useRef(null);
  const pendingIn = useRef(false);

  const advance = useCallback((nextIndex) => {
    setPhase('out');
    setTimeout(() => {
      pendingIn.current = true;
      setCurrentIndex(nextIndex);
    }, EXIT_MS);
  }, []);

  // After currentIndex updates the DOM, useLayoutEffect fires before paint.
  // We then schedule setPhase('in') in a rAF so the browser first paints
  // the new content at opacity:0, then transitions to opacity:1.
  useLayoutEffect(() => {
    if (!pendingIn.current) return;
    pendingIn.current = false;
    const raf = requestAnimationFrame(() => setPhase('in'));
    return () => cancelAnimationFrame(raf);
  }, [currentIndex]);

  const next = useCallback(() => {
    advance((currentIndex + 1) % quotes.length);
  }, [currentIndex, advance]);

  const prev = useCallback(() => {
    advance((currentIndex - 1 + quotes.length) % quotes.length);
  }, [currentIndex, advance]);

  const goTo = useCallback((index) => {
    if (index === currentIndex) return;
    advance(index);
  }, [currentIndex, advance]);

  useEffect(() => {
    if (isHovered) return;
    timerRef.current = setInterval(next, INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [isHovered, next]);

  const el = (delay) => ({
    opacity: phase === 'in' ? 1 : 0,
    transform: phase === 'in' ? 'translateY(0px)' : 'translateY(10px)',
    transition: phase === 'in'
      ? `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`
      : `opacity ${EXIT_MS}ms ease, transform ${EXIT_MS}ms ease`,
  });

  return (
    <Box
      position="relative"
      width="100%"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <VStack
        spacing={4}
        textAlign="center"
        width="100%"
        px={4}
        minH={{ base: "auto", md: "230px" }}
        justify="center"
        position="relative"
        zIndex={1}
      >
        <Text
          style={el(0)}
          fontSize={{ base: "xl", md: "2xl" }}
          fontFamily="Sanskrit2003"
          color="brand.300"
          letterSpacing="wide"
          fontWeight="medium"
          textShadow="0 1px 2px rgba(0,0,0,0.2)"
        >
          {quotes[currentIndex].sanskrit}
        </Text>

        <Text
          style={el(180)}
          fontSize={{ base: "md", md: "lg" }}
          fontStyle="italic"
          color="whiteAlpha.900"
          lineHeight="tall"
          maxW="3xl"
          fontWeight="medium"
        >
          "{quotes[currentIndex].text}"
        </Text>

        <Text
          style={el(420)}
          fontSize={{ base: "sm", md: "md" }}
          color="whiteAlpha.600"
          fontWeight="medium"
          letterSpacing="wider"
          mt={2}
        >
          — {quotes[currentIndex].author}
        </Text>
      </VStack>

      {/* Navigation */}
      <HStack justify="center" spacing={2} mt={6}>
        <IconButton
          icon={<FaChevronLeft size={10} />}
          size="xs"
          variant="ghost"
          color="whiteAlpha.400"
          _hover={{ color: 'brand.300', bg: 'transparent' }}
          onClick={prev}
          aria-label="Previous quote"
          minW="auto"
        />

        {quotes.map((_, i) => (
          <Box
            key={i}
            w={i === currentIndex ? '18px' : '6px'}
            h="5px"
            borderRadius="full"
            bg={i === currentIndex ? 'brand.400' : 'whiteAlpha.300'}
            cursor="pointer"
            onClick={() => goTo(i)}
            transition="all 0.35s ease"
            _hover={{ bg: i === currentIndex ? 'brand.400' : 'whiteAlpha.500' }}
          />
        ))}

        <IconButton
          icon={<FaChevronRight size={10} />}
          size="xs"
          variant="ghost"
          color="whiteAlpha.400"
          _hover={{ color: 'brand.300', bg: 'transparent' }}
          onClick={next}
          aria-label="Next quote"
          minW="auto"
        />
      </HStack>
    </Box>
  );
};

export default QuoteCarousel;
